import { useMemo, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';
import { NormalizedTask } from '../../domain/normalize';
import { computeKpis, countBy, serieMensual } from '../../domain/metrics';
import { UserDoc } from '../../domain/types';
import {
  Card, Kpi, DonutCard, BoardIntro,
  tooltipStyle, axisTick, GRID, CHART_CURSOR,
} from './shared';

type EvoMode = 'urgencia' | 'tipo';

export default function BoardTipos({ tasks }: { tasks: NormalizedTask[]; users: UserDoc[] }) {
  const [evoMode, setEvoMode] = useState<EvoMode>('urgencia');

  const k = useMemo(() => computeKpis(tasks), [tasks]);
  const activos = useMemo(() => tasks.filter((t) => !t.excluido), [tasks]);

  const tipoTarea = useMemo(() => countBy(activos, (t) => t.tipo || '(sin tipo)'), [activos]);
  const urgencia = useMemo(() => countBy(activos, (t) => t.urgencia || '(sin dato)'), [activos]);

  // Evolutivo mensual (conteo): dos pares según el modo elegido.
  const serie = useMemo(
    () => serieMensual(tasks).map((m) => ({ ...m, planificadas: m.tareas - m.urgentes, ordinarias: m.tareas - m.extraordinarias })),
    [tasks]
  );

  return (
    <div className="grid">
      <BoardIntro
        titulo="Tipos de trabajo y urgencias"
        objetivo="Qué tipo de producción pide la organización y cuánto llega planificado vs. urgente o fuera de lo previsto."
        preguntas={['¿Qué se pide más?', '¿Cuánto trabajo es urgente?', '¿Qué áreas generan más urgencias?']}
      />
      <div className="cards" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px,1fr))' }}>
        <Kpi label="Tareas" value={String(k.qtyTareas)} />
        <Kpi label="Urgentes" value={String(k.qtyUrgentes)} sub={`${k.pctUrgentes}% del total`} tone={k.pctUrgentes > 40 ? 'bad' : undefined} help="Urgencia alta o tareas extraordinarias, sobre el total." />
        <Kpi label="Extraordinarias" value={String(k.qtyExtraordinarias)} sub={`${k.pctExtraordinarias}% del total`} help="Pedidos fuera de lo planificado (tipo 'Extraordinaria'), sobre el total." />
      </div>

      <div className="cards">
        <DonutCard title="Tipo de tarea" hint="Ordinaria vs extraordinaria." data={tipoTarea} colors={['#4f46e5', '#b91c1c', '#cbd5e1']} />
        <DonutCard title="Urgencia" data={urgencia} colors={['#16a34a', '#f59e0b', '#b91c1c', '#cbd5e1']} />
      </div>

      <Card
        title={`Evolutivo mensual de tareas: ${evoMode === 'urgencia' ? 'planificadas vs urgentes' : 'ordinarias vs extraordinarias'}`}
        hint="Cantidad de tareas por mes de solicitud. Cambiá el par con el botón."
        action={
          <div className="metric-toggle" role="radiogroup" aria-label="Par a comparar">
            <button type="button" role="radio" aria-checked={evoMode === 'urgencia'} className={evoMode === 'urgencia' ? 'active' : ''} onClick={() => setEvoMode('urgencia')}>Planif. / Urgentes</button>
            <button type="button" role="radio" aria-checked={evoMode === 'tipo'} className={evoMode === 'tipo' ? 'active' : ''} onClick={() => setEvoMode('tipo')}>Ordin. / Extraord.</button>
          </div>
        }
      >
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={serie} margin={{ left: -10, right: 10, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
            <XAxis dataKey="mes" tick={axisTick} /><YAxis allowDecimals={false} tick={axisTick} />
            <Tooltip contentStyle={tooltipStyle} cursor={{ stroke: CHART_CURSOR }} /><Legend wrapperStyle={{ fontSize: 12 }} />
            {evoMode === 'urgencia' ? (
              <>
                <Line type="monotone" dataKey="planificadas" name="Planificadas" stroke="#4f46e5" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="urgentes" name="Urgentes" stroke="#b91c1c" strokeWidth={2} dot={{ r: 3 }} />
              </>
            ) : (
              <>
                <Line type="monotone" dataKey="ordinarias" name="Ordinarias" stroke="#4f46e5" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="extraordinarias" name="Extraordinarias" stroke="#db2777" strokeWidth={2} dot={{ r: 3 }} />
              </>
            )}
          </LineChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );
}
